import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import membersStrings from 'modules/members/strings';
import SectionDivider from 'modules/common/components/SectionDivider';
import { validationErrorType } from 'modules/members/enum';
import { updateUserPoints } from 'modules/members/actions';
import pointsForm from 'configs/pointsForm';

import {
  FormWrapper,
  PointsIcon,
  FormSection,
  InputWrapper,
  InputLabel,
  PointsInput,
  ValidationErrMsg,
  SelectInput,
  ReasonInput,
  Button,
  PhoneNumberInput,
} from './UpdatePointsForm.style';

const {
  awardPoints,
  deductPoints,
  award,
  deduct,
  points,
  addReason,
  requiredField,
  wrongField,
  selectCategory,
  reason,
  phoneNumber,
  outOfLimitPoiints,
} = membersStrings.memberProfile;

const { pointsAwardDeductLimit } = pointsForm;

const UpdatePointsForm = ({ toAddPoints, closeModal, toAddNewMember }) => {
  const dispatch = useDispatch();

  const { configurations, member } = useSelector(state => ({
    configurations: state.common.configurations,
    member: state.members.member,
  }));
  const servicesModels = configurations?.details?.servicesModels;
  const { details } = member;
  const userEmail = details?.data?.userEmail;

  const requiredFormFields = ['points', 'category', 'reason'];
  if (toAddNewMember) requiredFormFields.push('phoneNumber');

  const requiredFormFieldsWithRegexValidation = {
    phoneNumber: /^01[0-9]{9}$/i,
  };

  const [formFieldsValues, setFormFieldsValues] = useState({
    points: '',
    category: '',
    reason: '',
    phoneNumber: '',
  });
  const [formFieldsValidation, setFormFieldsValidation] = useState({
    points: { status: validationErrorType.valid, errMessage: '' },
    category: { status: validationErrorType.valid, errMessage: '' },
    reason: { status: validationErrorType.valid, errMessage: '' },
    phoneNumber: { status: validationErrorType.valid, errMessage: '' },
  });

  const handleInputFieldChange = (fieldName, fieldEvent) => {
    let { value } = fieldEvent.target;
    if (fieldName === 'points' && value) {
      value = Math.abs(parseInt(value)) || '';
    }

    if (fieldName === 'reason' && value) {
      value = value.toString().trim().length !== 0 ? value : '';
    }

    if (fieldName === 'phoneNumber' && value) {
      value = value.toString().trim().length !== 0 ? value : '';
    }

    setFormFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj[fieldName] = value;
      return fieldsObj;
    });
  };

  const submit = () => {
    const data = {
      email: toAddNewMember ? `002${formFieldsValues.phoneNumber}@vezeeta.com` : userEmail,
      serviceId: +formFieldsValues.category,
      language: 'ar-EG',
      countryId: 1,
      amount: +formFieldsValues.points,
      reason: formFieldsValues.reason,
    };
    dispatch(updateUserPoints({ shouldAward: toAddPoints, toAddNewMember, data }));
    setFormFieldsValues({ points: '', category: '', reason: '', phoneNumber: '' });
    closeModal();
  };

  const handleSubmitBtnClick = () => {
    let isFormValid = true;
    requiredFormFields.forEach(fieldName => {
      let isFieldValid = validationErrorType.valid;
      let errMessage = '';
      if (fieldName === 'points') {
        if (formFieldsValues[fieldName] > pointsAwardDeductLimit) {
          isFieldValid = validationErrorType.outOfLimit;
          isFormValid = false;
        }
        if (isFieldValid === validationErrorType.outOfLimit) errMessage = outOfLimitPoiints;
      }
      if (
        !formFieldsValues[fieldName].toString() ||
        (requiredFormFieldsWithRegexValidation[fieldName] &&
          requiredFormFieldsWithRegexValidation[fieldName].test(formFieldsValues[fieldName]) ===
            false)
      ) {
        isFieldValid =
          (!formFieldsValues[fieldName] && validationErrorType.empty) || validationErrorType.wrong;
        isFormValid = false;
        if (isFieldValid === validationErrorType.empty) {
          errMessage = requiredField;
        }
        if (isFieldValid === validationErrorType.wrong) {
          errMessage = wrongField;
        }
      }

      setFormFieldsValidation(prevState => ({
        ...prevState,
        [fieldName]: { ...prevState[fieldName], status: isFieldValid, errMessage },
      }));
    });

    if (isFormValid === false) return;

    // api call here
    submit();
  };

  const renderForm = () => (
    <FormWrapper>
      {toAddNewMember && (
        <>
          <FormSection>
            <InputLabel as="label">{phoneNumber}</InputLabel>
            <InputWrapper>
              <PhoneNumberInput
                type="number"
                placeholder={phoneNumber}
                value={formFieldsValues.phoneNumber}
                onChange={event => handleInputFieldChange('phoneNumber', event)}
                error={!!formFieldsValidation?.phoneNumber?.errMessage}
              />
            </InputWrapper>
            <ValidationErrMsg>{formFieldsValidation?.phoneNumber?.errMessage}</ValidationErrMsg>
          </FormSection>
          <SectionDivider gap={16} />
        </>
      )}
      <FormSection>
        <InputLabel as="label">{toAddPoints ? awardPoints : deductPoints}</InputLabel>
        <InputWrapper>
          <PointsIcon />
          <PointsInput
            placeholder="0"
            type="number"
            min="1"
            value={formFieldsValues.points}
            onChange={event => handleInputFieldChange('points', event)}
            error={!!formFieldsValidation?.points?.errMessage}
            data-testid="pointsInput"
          />
        </InputWrapper>
        <ValidationErrMsg>{formFieldsValidation?.points?.errMessage}</ValidationErrMsg>
      </FormSection>
      <SectionDivider gap={16} />
      <FormSection>
        <InputLabel as="label">{selectCategory}</InputLabel>
        <SelectInput
          value={formFieldsValues.category}
          onChange={event => handleInputFieldChange('category', event)}
          error={!!formFieldsValidation?.category?.errMessage}
          data-testid="categoryInput"
        >
          <option value="">{selectCategory}</option>
          {servicesModels?.map(service => (
            <option value={service.serviceId} key={service.serviceId}>
              {service.serviceName}
            </option>
          ))}
        </SelectInput>
        <ValidationErrMsg>{formFieldsValidation?.category?.errMessage}</ValidationErrMsg>
      </FormSection>
      <SectionDivider gap={16} />
      <FormSection>
        <InputLabel as="label">{reason}</InputLabel>
        <ReasonInput
          placeholder={addReason}
          value={formFieldsValues.reason}
          onChange={event => handleInputFieldChange('reason', event)}
          error={!!formFieldsValidation?.reason?.errMessage}
          data-testid="reasonInput"
        />
        <ValidationErrMsg>{formFieldsValidation?.reason?.errMessage}</ValidationErrMsg>
      </FormSection>
      <SectionDivider gap={16} />
      <Button data-testid="submitButton" toAddPoints={toAddPoints} onClick={handleSubmitBtnClick}>
        {toAddPoints ? award : deduct} {formFieldsValues.points || '0'} {points}
      </Button>
    </FormWrapper>
  );

  return renderForm();
};

UpdatePointsForm.propTypes = {
  toAddPoints: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
};

UpdatePointsForm.defaultProps = {
  toAddNewMember: false,
};

export default UpdatePointsForm;
